import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { ShieldAlert, CheckCircle2, XCircle, MinusCircle, RefreshCw, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SkeletonPage, { SkeletonCard, SkeletonTable } from "@/components/KovaSkeleton";

const RANGE_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "365", label: "Last 1 year" },
];

const ROLE_LABELS = {
  admin: "Admin", co_admin: "Co-Admin", accountant: "Accountant",
  sales_manager: "Sales Manager", sales_rep: "Sales Representative",
  marketing_head: "Marketing Head", marketing_rep: "Marketing Representative",
  service_manager: "Service Manager", spare_parts_manager: "Spare Parts Manager",
  technician: "Technician",
};

function DeliveryDot({ n }) {
  const total = n.sent + n.failed + n.skipped;
  if (total === 0) return <span className="text-xs text-[#8A8F98]">No admins to notify</span>;
  return (
    <div className="flex items-center gap-3 text-xs font-mono">
      <span className="inline-flex items-center gap-1 text-[#39FF14]" title="Delivered">
        <CheckCircle2 size={12} /> {n.sent}
      </span>
      <span className="inline-flex items-center gap-1 text-red-600" title="Failed">
        <XCircle size={12} /> {n.failed}
      </span>
      <span className="inline-flex items-center gap-1 text-[#8A8F98]" title="Skipped (no phone)">
        <MinusCircle size={12} /> {n.skipped}
      </span>
    </div>
  );
}

export default function SecurityLog() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/security-events?days=${days}`);
      setEvents(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load security events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user, days]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="p-8 lg:p-10 max-w-[1400px]">
      <PageHeader
        title="Security Log"
        subtitle="Every account auto-lock triggered by too many failed sign-in attempts, with delivery status per admin alert."
        testid="page-security-log-header"
        action={
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="security-range-select" className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              data-testid="security-refresh-btn"
              className="border-[#3A4640]"
            >
              <RefreshCw size={14} className="mr-2" /> Refresh
            </Button>
          </div>
        }
      />

      <section className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm overflow-hidden">
        <header className="px-5 py-4 border-b border-[#3A4640] flex items-center gap-3">
          <div className="w-9 h-9 rounded-md flex items-center justify-center text-white" style={{ background: "#991b1b" }}>
            <ShieldAlert size={16} />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-[#F8F9FA]">Auto-lock events</div>
            <div className="text-xs text-[#8A8F98]">Sorted newest first. Click a row to expand delivery details.</div>
          </div>
          <div className="text-xs font-heading uppercase tracking-wider text-[#8A8F98]" data-testid="security-events-count">
            {events.length} event{events.length === 1 ? "" : "s"}
          </div>
        </header>

        {loading ? (
          <SkeletonTable rows={8} cols={5} />
        ) : events.length === 0 ? (
          <div className="px-5 py-12 text-center text-[#8A8F98] text-sm" data-testid="security-empty">
            No auto-lock events in the selected range. Your team is safe.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0B0F0C] border-b border-[#3A4640] text-xs uppercase tracking-wider text-[#8A8F98] font-heading">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Locked account</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Trigger</th>
                <th className="text-left px-4 py-3">Admin alerts</th>
              </tr>
            </thead>
            <tbody data-testid="security-events-tbody">
              {events.map((e) => {
                const expanded = expandedId === e.id;
                return (
                  <>
                    <tr
                      key={e.id}
                      className="border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C] cursor-pointer"
                      data-testid={`security-row-${e.id}`}
                      onClick={() => setExpandedId(expanded ? null : e.id)}
                    >
                      <td className="px-4 py-3 text-[#F8F9FA]">{formatDate(e.occurred_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#F8F9FA]">{e.user_name || "(no name)"}</div>
                        <div className="font-mono text-xs text-[#8A8F98]">{e.user_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-[#121815] text-[#F8F9FA] px-2 py-1 rounded font-heading">{ROLE_LABELS[e.user_role] || e.user_role}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#F8F9FA]">
                        <span className="inline-flex items-center gap-1 text-red-300">
                          <Ban size={12} /> {e.attempts} fails / {e.window_minutes} min
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <DeliveryDot n={e.delivery_summary || { sent: 0, failed: 0, skipped: 0, admin_count: 0 }} />
                      </td>
                    </tr>
                    {expanded && (
                      <tr key={`${e.id}-details`} className="border-b border-[#3A4640] bg-[#0B0F0C]/60" data-testid={`security-details-${e.id}`}>
                        <td colSpan={5} className="px-6 py-4">
                          {(e.notified || []).length === 0 ? (
                            <div className="text-xs text-[#8A8F98]">No admin recipients recorded.</div>
                          ) : (
                            <div className="space-y-2">
                              {(e.notified || []).map((n, idx) => (
                                <div key={idx} className="text-xs bg-[#121815] border border-[#3A4640] rounded p-3">
                                  <div className="flex flex-wrap items-center gap-3">
                                    <span className="font-mono text-[#F8F9FA]">{n.email || n.admin_id}</span>
                                    {n.phone && <span className="font-mono text-[#8A8F98]">· {n.phone}</span>}
                                    {n.status === "skipped" && (
                                      <span className="inline-flex items-center gap-1 text-yellow-400"><MinusCircle size={12} /> Skipped — {n.reason}</span>
                                    )}
                                  </div>
                                  {(n.deliveries || []).length > 0 && (
                                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                                      {n.deliveries.map((d, di) => (
                                        <div key={di} className="border border-[#3A4640] rounded px-2 py-1.5 bg-[#0B0F0C]">
                                          <div className="flex items-center justify-between">
                                            <span className="uppercase font-heading tracking-wider text-[10px] text-[#8A8F98]">{d.channel}</span>
                                            {d.status === "sent" ? (
                                              <span className="inline-flex items-center gap-1 text-[#39FF14]"><CheckCircle2 size={11} /> sent</span>
                                            ) : d.status === "failed" ? (
                                              <span className="inline-flex items-center gap-1 text-red-600"><XCircle size={11} /> failed</span>
                                            ) : (
                                              <span className="inline-flex items-center gap-1 text-[#8A8F98]"><MinusCircle size={11} /> {d.status}</span>
                                            )}
                                          </div>
                                          {d.error && <div className="text-red-600 mt-1 break-words">{d.error}</div>}
                                          {d.sid && <div className="font-mono text-[#8A8F98] mt-1 break-all">SID: {d.sid}</div>}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
